import { StandardEvents } from '@shopify/events';

/**
 * Köttbutiken: köttlådans nyckeltal (snippets/kb-lada-fakta.liquid) i blocket Lådan i korthet och sektionen
 * Lådans innehåll. Byter vikt, portioner och kilopris när kunden väljer en annan variant (talen för alla varianter
 * ligger som JSON i elementet) och räknar hur många middagar lådan räcker till för valt antal personer.
 * Lyssnar på dokumentet, eftersom sektionen ligger utanför produktinfon där variantväljaren finns.
 */
class KbLada extends HTMLElement {
  #avbryt = new AbortController();
  /** @type {Record<string, {vikt: string, vikt_g: number, portioner: string, kilopris: string, portionspris: string}>} */
  #varianter = {};
  #personer = 4;

  connectedCallback() {
    try {
      this.#varianter = JSON.parse(this.querySelector('script[data-kb-lada-varianter]')?.textContent || '{}');
    } catch {
      this.#varianter = {};
    }

    const vald = /** @type {HTMLElement | null} */ (this.querySelector('[data-personer][aria-checked="true"]'));
    this.#personer = Number(vald?.dataset.personer) || 4;

    const { signal } = this.#avbryt;
    document.addEventListener(StandardEvents.productSelect, this.#vidVariantbyte, { signal });
    this.addEventListener('click', this.#vidKlick, { signal });
    this.addEventListener('keydown', this.#vidTangent, { signal });
  }

  disconnectedCallback() {
    this.#avbryt.abort();
  }

  /** @param {any} event */
  #vidVariantbyte = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then((/** @type {any} */ { detail }) => {
        if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
        const id = detail?.resource?.id;
        if (id) this.#visaVariant(String(id));
      })
      .catch(() => {});
  };

  /** @param {string} id */
  #visaVariant(id) {
    const v = this.#varianter[id];
    if (!v) return;
    for (const falt of this.querySelectorAll('[data-kb-lada-falt]')) {
      const nyckel = /** @type {keyof typeof v} */ (falt.getAttribute('data-kb-lada-falt'));
      if (nyckel in v) falt.textContent = String(v[nyckel]);
    }
    if (this.hasAttribute('data-vikt-g')) {
      this.dataset.viktG = String(v.vikt_g);
      this.#rakna();
    }
  }

  /** @param {MouseEvent} event */
  #vidKlick = (event) => {
    const knapp = event.target instanceof Element ? event.target.closest('[data-personer]') : null;
    if (knapp instanceof HTMLElement) this.#valjPersoner(knapp);
  };

  /** @param {KeyboardEvent} event */
  #vidTangent = (event) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-personer]')) return;
    const knappar = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-personer]')]);
    const nu = knappar.indexOf(event.target);
    let ny = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') ny = Math.min(nu + 1, knappar.length - 1);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') ny = Math.max(nu - 1, 0);
    if (ny < 0) return;
    event.preventDefault();
    const knapp = knappar[ny];
    if (!knapp) return;
    this.#valjPersoner(knapp);
    knapp.focus();
  };

  /** @param {HTMLElement} knapp */
  #valjPersoner(knapp) {
    this.#personer = Number(knapp.dataset.personer) || 1;
    for (const annan of this.querySelectorAll('[data-personer]')) {
      const vald = annan === knapp;
      annan.setAttribute('aria-checked', String(vald));
      annan.setAttribute('tabindex', vald ? '0' : '-1');
    }
    this.#rakna();
  }

  #rakna() {
    const raknare = /** @type {HTMLElement | null} */ (this.querySelector('[data-kb-raknare]'));
    if (!raknare) return;
    const vikt = Number(this.dataset.viktG) || 0;
    const portion = Number(this.dataset.portion) || 150;
    const middagar = Math.floor(vikt / portion / this.#personer);
    const tal = raknare.querySelector('[data-kb-middagar]');
    if (tal) tal.textContent = String(middagar);
    const text = raknare.querySelector('[data-kb-personer-text]');
    if (text) {
      const ord = this.#personer === 1 ? raknare.dataset.ett : raknare.dataset.flera;
      text.textContent = `${this.#personer} ${ord ?? ''}`.trim();
    }
  }
}

if (!customElements.get('kb-lada')) {
  customElements.define('kb-lada', KbLada);
}
